import { useAutoFollowMatch } from "@/hooks/useAutoFollowMatch";
import { uiText } from "@/lib/i18n/uiText";
import { strategyCounts, strategiesForPattern } from "@/lib/strategyRegistry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Shield, 
  BarChart3, 
  History,
  Target,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  Activity,
  Flame,
  Timer,
  LayoutGrid,
  Flag,
  DollarSign,
  Repeat,
  Shuffle,
  Crosshair,
  CircleDot,
  Square,
  ArrowUpDown,
  Hand,
  Move,
  Save,
  Hourglass,
  Layers,
  ExternalLink,
  Lock,
  Coins,
  Loader2
} from "lucide-react";
import { useNavigate, useLocation } from "@/lib/router-compat";
import { authPath } from "@/lib/authRedirect";
import { formatLocalTimeWithZone } from "@/lib/formatLocalTime";
import type { Prediction } from "@/types";
import { ConfidenceMeter } from "./ConfidenceMeter";
import { RiskBadge } from "./RiskBadge";
import { OddsBadge } from "./OddsBadge";
import { PredictionDiagnosticsPanel } from "./PredictionDiagnosticsPanel";
import { MatchStatsBlock } from "./MatchStatsBlock";
import { PredictionPageLinks } from "@/components/seo/PredictionPageLinks";
import { useCoins } from "@/hooks/useCoins";
import { usePredictionGate } from "@/hooks/usePredictionGate";
import {
  ANALYSIS_CREDIT_COST,
  REVEAL_CREDIT_COST,
} from "@/lib/creditPricing";
import { toast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PredictionDetailModalProps {
  prediction: Prediction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Strategy metadata for the signals grid
const ALL_STRATEGIES = [
  { id: 'Market Elasticity Ranking', name: 'Market Elasticity', icon: DollarSign, category: 'Pricing', description: 'Analyzes market elasticity and value opportunities' },
  { id: 'Scoreline Signature', name: 'Scoreline Signature', icon: Target, category: 'Team Behavior', description: 'Identifies teams with specific scoreline tendencies' },
  { id: 'Defensive Collapse Index', name: 'Defensive Collapse', icon: Shield, category: 'Team Behavior', description: 'Measures defensive vulnerability patterns' },
  { id: 'Motivation Asymmetry', name: 'Motivation Asymmetry', icon: Flame, category: 'Context', description: 'Detects motivation imbalances between teams' },
  { id: 'Schedule Fatigue', name: 'Schedule Fatigue', icon: Timer, category: 'Context', description: 'Analyzes fixture congestion and fatigue impact' },
  { id: 'Goal Distribution Skew', name: 'Goal Distribution', icon: BarChart3, category: 'Team Behavior', description: 'Identifies skewed goal-scoring patterns' },
  { id: 'Half Imbalance', name: 'Half Imbalance', icon: Hourglass, category: 'Team Behavior', description: 'Detects 1st vs 2nd half performance gaps' },
  { id: 'Reputation Bias', name: 'Reputation Bias', icon: Flag, category: 'Pricing', description: 'Exploits odds mispricing based on team reputation' },
  { id: 'Odds Movement', name: 'Odds Movement', icon: ArrowUpDown, category: 'Pricing', description: 'Tracks significant pre-match odds changes' },
  { id: 'High-Odds Repeatability', name: 'HHRF Filter', icon: Repeat, category: 'Pricing', description: 'Identifies reliable high-odds patterns' },
  { id: 'Controlled Chaos Leagues', name: 'Controlled Chaos', icon: Shuffle, category: 'Context', description: 'Identifies high-volatility league characteristics' },
  { id: 'Correct Score Trigger', name: 'Correct Score', icon: Crosshair, category: 'Scoring', description: 'Pattern-triggered correct score predictions' },
  { id: 'Underdog Situational', name: 'Underdog Advantage', icon: TrendingUp, category: 'Context', description: 'Detects favorable underdog situations' },
  { id: 'Combo Market Builder', name: 'Combo Builder', icon: Layers, category: 'Extended', description: 'AI-driven combination market analysis' },
  { id: 'Corner Patterns', name: 'Corner Patterns', icon: LayoutGrid, category: 'Extended', description: 'Analyzes corner kick tendencies and patterns' },
  { id: 'First Half Dominance', name: '1st Half Dominance', icon: Timer, category: 'Half Markets', description: 'Identifies first-half dominant performances' },
  { id: 'Second Half Surge', name: '2nd Half Surge', icon: Zap, category: 'Half Markets', description: 'Detects second-half goal surges' },
  { id: 'Shots Volume', name: 'Shots Analysis', icon: CircleDot, category: 'Extended', description: 'Analyzes shots volume and accuracy patterns' },
  { id: 'Fouls & Cards', name: 'Fouls & Cards', icon: Square, category: 'Extended', description: 'Predicts disciplinary patterns' },
  { id: 'Offsides Tendency', name: 'Offsides Tendency', icon: Move, category: 'Extended', description: 'Identifies offside-prone attacking patterns' },
  { id: 'Goalkeeper Saves', name: 'GK Saves', icon: Hand, category: 'Extended', description: 'Analyzes goalkeeper save tendencies' },
  { id: 'Goal Timing', name: 'Goal Timing', icon: Clock, category: 'Scoring', description: 'Predicts goal-scoring time windows' },
  { id: 'Extended Combos', name: 'Extended Combos', icon: Activity, category: 'Extended', description: 'Multi-signal combination analysis' }
];

const CATEGORY_COLORS: Record<string, string> = {
  'Pricing': 'bg-green-500/10 border-green-500/30 text-green-600',
  'Team Behavior': 'bg-blue-500/10 border-blue-500/30 text-blue-600',
  'Context': 'bg-amber-500/10 border-amber-500/30 text-amber-600',
  'Scoring': 'bg-purple-500/10 border-purple-500/30 text-purple-600',
  'Half Markets': 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600',
  'Extended': 'bg-pink-500/10 border-pink-500/30 text-pink-600'
};

// Strategy card component
function StrategyCard({ 
  strategy, 
  isTriggered 
}: { 
  strategy: typeof ALL_STRATEGIES[0];
  isTriggered: boolean;
}) {
  const Icon = strategy.icon;
  const colorClass = CATEGORY_COLORS[strategy.category] || 'bg-muted border-border';
  
  return (
    <div className={`p-3 rounded-lg border transition-all ${
      isTriggered 
        ? `${colorClass} shadow-xs` 
        : 'bg-muted/30 border-border/50 opacity-50'
    }`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1 rounded ${isTriggered ? 'bg-current/10' : 'bg-muted'}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs font-medium truncate">{uiText(strategy.name)}</span>
        {isTriggered && (
          <CheckCircle2 className="h-3 w-3 text-green-600 ml-auto flex-shrink-0" />
        )}
      </div>
      <p className="text-[10px] text-muted-foreground line-clamp-2">{uiText(strategy.description)}</p>
    </div>
  );
}

// Safety rating badge
function SafetyBadge({ rating }: { rating: string }) {
  const colors: Record<string, string> = {
    'Very High': 'bg-green-600 text-white',
    'High': 'bg-emerald-500 text-white',
    'Medium': 'bg-amber-500 text-white',
    'Low': 'bg-red-500 text-white'
  };
  
  return (
    <Badge className={`${colors[rating] || 'bg-gray-500'} gap-1`}>
      <Shield className="h-3 w-3" />
      {rating}  {uiText("Safety")} </Badge>
  );
}

function MetricRow({ label, value, suffix }: { label: string; value: number | string; suffix?: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}{suffix}</span>
    </div>
  );
}

export function PredictionDetailModal({ prediction, open, onOpenChange }: PredictionDetailModalProps) {
  useAutoFollowMatch(prediction ? {
    matchKey: `football:${prediction.match_id ?? prediction.id}`,
    sport: "football",
    homeTeam: prediction.team_home ?? "",
    awayTeam: prediction.team_away ?? "",
    startTime: prediction.kickoff_time ?? null,
    competition: prediction.league ?? null,
  } : null, open);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useCoins();
  // One shared gate for the pick (free once per day, then 2 credits) and one for
  // the analysis (always 1 credit, played or upcoming).
  const evaluated = prediction?.was_correct === true || prediction?.was_correct === false;
  const cardGate = usePredictionGate(prediction?.id, { kind: "card", evaluated, kickoff: prediction?.kickoff_time });
  const analysisGate = usePredictionGate(prediction?.id, { kind: "analysis", evaluated, kickoff: prediction?.kickoff_time });

  // Proprietary strategy intelligence (strategy_signals / pattern_references) is
  // NOT exposed to anonymous visitors via the public list queries. For signed-in
  // users we fetch the full analysis on demand when the modal opens.
  const { data: fullDetail } = useQuery({
    queryKey: ["prediction-full-detail", prediction?.id, isLoggedIn],
    enabled: open && !!prediction?.id && isLoggedIn,
    queryFn: async () => {
      // Server-side paywall enforcement: this RPC only returns the proprietary
      // strategy_signals / pattern_references if the caller is an admin or has
      // spent a coin to unlock this specific prediction. Direct table reads of
      // these columns are revoked for anon/authenticated.
      const { data, error } = await supabase.rpc("get_prediction_intelligence", {
        _prediction_id: prediction!.id,
      });
      if (error) throw error;
      return (data ?? null) as {
        strategy_signals?: Prediction["strategy_signals"];
        pattern_references?: Prediction["pattern_references"];
      } | null;
    },
  });

  // Navigation to Match Preview
  const handleViewMatchPreview = () => {
    if (!prediction) return;
    const params = new URLSearchParams({
      home: prediction.team_home || '',
      away: prediction.team_away || '',
      league: prediction.league || ''
    });
    onOpenChange(false);
    navigate(`/match-preview?${params.toString()}`);
  };

  if (!prediction) return null;

  // Merge on-demand full analysis (signed-in users) over the sanitized record.
  const merged = {
    ...prediction,
    strategy_signals:
      (fullDetail?.strategy_signals as unknown as Prediction["strategy_signals"]) ??
      prediction.strategy_signals,
    pattern_references:
      (fullDetail?.pattern_references as unknown as Prediction["pattern_references"]) ??
      prediction.pattern_references,
  };

  // Card gate: the pick stays hidden until the daily free reveal or 2 credits.
  const locked = cardGate.locked;
  const handleReveal = () => {
    if (!isLoggedIn && !cardGate.freeAvailable) onOpenChange(false);
    cardGate.act();
  };

  // Analysis is a separate 1-credit unlock, for played and upcoming matches.
  const strategyLocked = analysisGate.locked;
  const handleUnlockStrategy = () => {
    if (!isLoggedIn) onOpenChange(false);
    analysisGate.act();
  };


  if (locked) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <Badge variant="outline" className="mb-2 w-fit text-xs">{prediction.league}</Badge>
            <DialogTitle className="text-xl">
              {prediction.team_home}  {uiText("vs")} {prediction.team_away}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {formatLocalTimeWithZone(prediction.kickoff_time, "EEEE, MMMM d, yyyy 'at' HH:mm", "Time TBD")}
            </p>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="rounded-full bg-muted/60 p-4">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">{uiText("The prediction, odds & full analysis are hidden")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                 {uiText("Sign in and spend")}{" "}
                {REVEAL_CREDIT_COST}  {uiText("credits, to unlock this pick. Analysis costs")}{" "}
                {ANALYSIS_CREDIT_COST}  {uiText("credit.")} </p>
            </div>
            <Button onClick={handleReveal} disabled={cardGate.pending} className="gap-2 min-h-11">
              {cardGate.pending ? <Loader2 className="h-4 w-4 animate-spin" /> : isLoggedIn ? <Coins className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {uiText(cardGate.label)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }



  // Parse explanation for display
  const explanationParts = prediction.explanation?.split(' | ') || [];
  const primaryReason = explanationParts[0]?.replace('📊 ', '') || '';
  const supportingFactors = explanationParts.filter(p => p.startsWith('✓')).map(p => p.replace('✓ ', ''));
  const riskFactors = explanationParts.filter(p => p.startsWith('⚠️')).map(p => p.replace('⚠️ ', ''));

  // Parse strategy signals from explanation - NEW ROBUST PARSING
  const parseTriggeredStrategies = (): string[] => {
    // First check structured data
    if (merged.strategy_signals?.triggered_strategies) {
      return merged.strategy_signals.triggered_strategies;
    }
    
    // Parse from explanation format: "🔧 Strategies: [Strategy 1, Strategy 2, ...]"
    const strategiesMatch = prediction.explanation?.match(/Strategies:\s*\[([^\]]+)\]/);
    if (strategiesMatch?.[1]) {
      return strategiesMatch[1].split(',').map(s => s.trim());
    }
    
    // No stored list: show nothing rather than guessing strategy names.
    return [];
  };

  const triggeredStrategies = parseTriggeredStrategies();
  const engineCounts = strategyCounts(
    merged.strategy_signals as Parameters<typeof strategyCounts>[0],
    ALL_STRATEGIES.length,
  );
  // Unentitled viewers get no strategy_signals; fall back to the public explanation.
  const explMatch = prediction.explanation?.match(/(\d+)\s*\/\s*\d+\s*strategies/i);
  const strategyCount = engineCounts.triggered ||
    (explMatch ? Number(explMatch[1]) : 0) || triggeredStrategies.length;
  const strategyTotal = engineCounts.total;
  
  // Parse safety rating from explanation
  const parseSafetyRating = (): string => {
    if (merged.strategy_signals?.safety_rating) {
      return merged.strategy_signals.safety_rating;
    }
    const safetyMatch = prediction.explanation?.match(/Safety:\s*(Very High|High|Medium|Low)/i);
    return safetyMatch?.[1] || 'Medium';
  };
  
  // Parse cumulative score from explanation
  const parseCumulativeScore = (): number => {
    if (merged.strategy_signals?.cumulative_score) {
      return merged.strategy_signals.cumulative_score;
    }
    const scoreMatch = prediction.explanation?.match(/Score:\s*(\d+)/);
    return scoreMatch ? parseInt(scoreMatch[1]!) : Math.round(prediction.confidence_score * 0.9);
  };
  
  // Parse probability estimate from explanation
  const parseProbabilityEstimate = (): number => {
    if (merged.strategy_signals?.probability_estimate) {
      return merged.strategy_signals.probability_estimate;
    }
    const probMatch = prediction.explanation?.match(/Win:\s*(\d+)%/);
    return probMatch ? parseInt(probMatch[1]!) : Math.round(prediction.confidence_score * 0.6);
  };
  
  const safetyRating = parseSafetyRating();
  const cumulativeScore = parseCumulativeScore();
  const probabilityEstimate = parseProbabilityEstimate();

  // Outcome badge
  const OutcomeBadge = () => {
    if (prediction.was_correct === true) {
      return (
        <Badge className="bg-green-600 text-white gap-1">
          <CheckCircle2 className="h-3 w-3" />
           {uiText("Won")} </Badge>
      );
    }
    if (prediction.was_correct === false) {
      return (
        <Badge className="bg-red-600 text-white gap-1">
          <XCircle className="h-3 w-3" />
           {uiText("Lost")} </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
         {uiText("Pending")} </Badge>
    );
  };

  // Group strategies by category
  const strategiesByCategory = ALL_STRATEGIES.reduce((acc, strategy) => {
    if (!acc[strategy.category]) {
      acc[strategy.category] = [];
    }
    acc[strategy.category]!.push(strategy);
    return acc;
  }, {} as Record<string, typeof ALL_STRATEGIES>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full sm:w-[95vw] sm:max-w-3xl h-[92dvh] max-h-[92dvh] sm:h-[90vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6 max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-t-2xl max-sm:rounded-b-none max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <Badge variant="outline" className="mb-2 text-xs">
                {prediction.league}
              </Badge>
              <DialogTitle className="text-xl">
                {prediction.team_home}  {uiText("vs")} {prediction.team_away}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {formatLocalTimeWithZone(prediction.kickoff_time, "EEEE, MMMM d, yyyy 'at' HH:mm", "Time TBD")}
              </p>
            </div>
            <OutcomeBadge />
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 -mr-2 pr-4 overflow-y-auto">
          <div className="space-y-4">
            {/* Prediction Summary */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-muted-foreground">{uiText("Predicted Outcome")}</p>
                  <p className="text-lg font-bold text-primary">{uiText(prediction.predicted_outcome)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <OddsBadge
                    odds={prediction.odds}
                    size="lg"
                    animated
                    estimated={merged.strategy_signals?.data_thin === true}
                  />
                  <RiskBadge level={prediction.risk_level} size="md" />
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Badge variant="secondary">{uiText(prediction.market_type)}</Badge>
                <SafetyBadge rating={safetyRating} />
                <Badge variant="outline" className="gap-1">
                  <Zap className="h-3 w-3" />
                  {strategyCount}/{strategyTotal}  {uiText("strategies")} </Badge>
              </div>

              <ConfidenceMeter score={prediction.confidence_score} size="lg" />
              
              {prediction.stake_recommendation && (
                <p className="text-xs text-muted-foreground mt-2">
                   {uiText("💰 Stake:")} {prediction.stake_recommendation}
                </p>
              )}
            </div>

            {/* Why this bet — plain-language rationale + calibration breakdown.
                Always visible once the prediction is revealed, before the
                separate strategy-insights unlock. */}
            <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                 {uiText("Why this bet")} </h3>
              <p className="text-sm text-muted-foreground">
                 {uiText("Our engine backs")}{" "}
                <span className="font-medium text-foreground">{uiText(prediction.predicted_outcome)}</span>{" "}
                 {uiText("in the")} {uiText(prediction.market_type)}  {uiText("market at")}{" "}
                <span className="font-medium text-foreground">{prediction.odds?.toFixed(2)}</span>{" "}
                 {uiText("odds — a")} {prediction.confidence_score}{uiText("% confidence call rated")}{" "}
                {uiText(prediction.risk_level)}  {uiText("risk")} {strategyCount > 0 && `, with ${strategyCount}/${strategyTotal} strategies confirmed`}.
              </p>
              {primaryReason && (
                <p className="text-sm text-muted-foreground">{primaryReason}</p>
              )}

              {/* Calibration breakdown */}
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div className="rounded-lg bg-background/60 p-2 text-center">
                  <p className="text-lg font-bold text-primary">{prediction.confidence_score}%</p>
                  <p className="text-[10px] text-muted-foreground">{uiText("Model confidence")}</p>
                </div>
                <div className="rounded-lg bg-background/60 p-2 text-center">
                  <p className="text-lg font-bold text-green-600">
                    {strategyCount > 0 ? `${strategyCount}/${strategyTotal}` : "—"}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{uiText("Strategies confirmed")}</p>
                </div>
                <div className="rounded-lg bg-background/60 p-2 text-center">
                  <p className="text-lg font-bold text-amber-600">{safetyRating}</p>
                  <p className="text-[10px] text-muted-foreground">{uiText("Safety rating")}</p>
                </div>
              </div>

              {merged.strategy_signals?.data_thin === true && (
                <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                   {uiText("Odds are engine-estimated (marked “est.”) — limited live market data was available for this fixture.")} </p>
              )}
              <p className="text-xs text-muted-foreground border-t border-border/60 pt-2">
                 {uiText("No prediction is guaranteed — sport is unpredictable. Treat this as data-informed guidance and always stake responsibly.")} </p>
            </div>

            <Separator />



            {strategyLocked ? (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 text-center flex flex-col items-center gap-3">
                <div className="rounded-full bg-muted/60 p-3">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{uiText("Strategy insights are locked")}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                     {uiText("Unlock the")} {strategyTotal}{uiText("-strategy engine, diagnostics & pattern matches for this match.")} </p>
                </div>
                <Button onClick={handleUnlockStrategy} disabled={analysisGate.pending} className="gap-2 min-h-11">
                  {analysisGate.pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                  {uiText(analysisGate.label)}
                </Button>
              </div>
            ) : (
            <>
            {/* Cumulative Strategy Score */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4">
              <h3 className="font-semibold text-lg flex items-center gap-2 mb-3">
                <Layers className="h-5 w-5 text-primary" />
                 {uiText("Cumulative")} {strategyTotal}{uiText("-Strategy Engine")} </h3>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-background/60 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{strategyCount}</p>
                  <p className="text-xs text-muted-foreground">{uiText("Strategies Triggered")}</p>
                </div>
                <div className="text-center p-3 bg-background/60 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{cumulativeScore}</p>
                  <p className="text-xs text-muted-foreground">{uiText("Cumulative Score")}</p>
                </div>
                <div className="text-center p-3 bg-background/60 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{probabilityEstimate}%</p>
                  <p className="text-xs text-muted-foreground">{uiText("Win Probability")}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-muted-foreground">{uiText("Strategy Confirmation:")}</span>
                <Progress value={(strategyCount / Math.max(strategyTotal, 1)) * 100} className="flex-1 h-2" />
                <span className="text-sm font-medium">{Math.round((strategyCount / Math.max(strategyTotal, 1)) * 100)}%</span>
              </div>
              
              <p className="text-xs text-muted-foreground">
                {strategyCount >= 8 && "✅ Very High safety - 8+ strategies confirmed"}
                {strategyCount >= 5 && strategyCount < 8 && "✓ High safety - 5-7 strategies confirmed"}
                {strategyCount >= 3 && strategyCount < 5 && "⚠️ Moderate safety - 3-4 strategies confirmed"}
                {strategyCount < 3 && "⚡ Low confirmation - Enhanced risk/reward"}
              </p>
            </div>

            <Separator />

            {/* Per-prediction strategy diagnostics */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                 {uiText("Strategy Diagnostics")} </h3>
              <PredictionDiagnosticsPanel prediction={merged as Prediction} />
            </div>

            <MatchStatsBlock matchId={prediction.match_id} home={prediction.team_home ?? ""} away={prediction.team_away ?? ""} />

            <Separator />

            {/* Strategies Grid */}

            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                 {uiText("All")} {strategyTotal}  {uiText("Strategy Signals")} </h3>

              {Object.entries(strategiesByCategory).map(([category, strategies]) => (
                <div key={category} className="space-y-2">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Badge variant="outline" className={CATEGORY_COLORS[category]?.split(' ')[0] || ''}>
                      {category}
                    </Badge>
                    <span className="text-muted-foreground text-xs">
                      ({strategies.filter(s => triggeredStrategies.some(t => 
                        t.toLowerCase().includes(s.id.toLowerCase()) || 
                        t.toLowerCase().includes(s.name.toLowerCase()) ||
                        s.id.toLowerCase().includes(t.toLowerCase())
                      )).length}/{strategies.length}  {uiText("active)")} </span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {strategies.map(strategy => {
                      const isTriggered = triggeredStrategies.some(t => 
                        t.toLowerCase().includes(strategy.id.toLowerCase()) || 
                        t.toLowerCase().includes(strategy.name.toLowerCase()) ||
                        strategy.id.toLowerCase().includes(t.toLowerCase())
                      );
                      return (
                        <StrategyCard 
                          key={strategy.id} 
                          strategy={strategy} 
                          isTriggered={isTriggered} 
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Pattern References */}
            {merged.pattern_references && merged.pattern_references.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                     {uiText("Pattern Matches (")}{merged.pattern_references.length})
                  </h3>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {merged.pattern_references.map((ref, i) => {
                      const by = strategiesForPattern(ref.pattern_name, triggeredStrategies);
                      return (
                        <div key={i} className="rounded-md border border-border p-2 text-xs">
                          <div className="flex items-center gap-1 font-medium">
                            <CheckCircle2 className="h-3 w-3 text-primary" />
                            {ref.pattern_name} ({ref.similarity_score}%)
                          </div>
                          <p className="mt-1 text-muted-foreground">
                            {by.length > 0 ? uiText("Triggered by: {{v0}}", { v0: by.join(", ") }) : uiText("No linked strategy fired for this pattern")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            </>
            )}

            {/* Analysis Summary */}
            {(primaryReason || supportingFactors.length > 0 || riskFactors.length > 0) && (
              <>
                <Separator />
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  {primaryReason && (
                    <div>
                      <p className="text-sm font-medium mb-1">{uiText("📊 Analysis Summary")}</p>
                      <p className="text-sm text-muted-foreground">{primaryReason}</p>
                    </div>
                  )}
                  
                  {supportingFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-600 mb-1">{uiText("Supporting Factors:")}</p>
                      <div className="space-y-1">
                        {supportingFactors.map((factor, i) => (
                          <p key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
                            {factor}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {riskFactors.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-600 mb-1">{uiText("Risk Factors:")}</p>
                      <div className="space-y-1">
                        {riskFactors.map((factor, i) => (
                          <p key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" />
                            {factor}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <Separator />

            {/* Internal links: league hub, team pages, guides, previous predictions */}
            <PredictionPageLinks prediction={prediction} />
          </div>
        </ScrollArea>

        
        <DialogFooter className="border-t pt-4">
          <Button 
            variant="outline" 
            onClick={handleViewMatchPreview}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
             {uiText("View Full Match Preview")} </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
